/**
 * Définition de la navigation.
 *
 * Partagée entre la barre latérale (`MenuV2`) et les raccourcis du tableau de
 * bord : c'est la seule table qui associe un écran à la permission qui l'ouvre,
 * la dupliquer garantirait qu'un des deux se désynchronise.
 *
 * Les types de formulaire non listables sont fusionnés dynamiquement par
 * `MenuV2.visibleItems`, ils n'ont pas leur place ici.
 */

export const NAV = [
    { type: 'link', label: 'menu.dashboard', icon: 'fal fa-th', to: '/', exact: true },
    {
        type: 'group',
        label: 'menu.tickets.ticket',
        icon: 'fal fa-ticket',
        permission: 'posts.view',
        key: 'tickets',
        children: [
            { type: 'link', label: 'menu.tickets.all', to: '/posts', exact: true },
            // Onglet à part, borné par le filtre serveur
            // (`filter[contribution]=1`) et non par un tri de la page déjà
            // paginée. N'a de sens que si la commune exploite au moins un
            // formulaire de propositions citoyennes.
            { type: 'link', label: 'menu.tickets.contributions', to: '/contributions', exact: true, requiresContributionForm: true },
            { type: 'link', label: 'menu.tickets.new', to: '/posts?status=new' },
            { type: 'link', label: 'menu.tickets.open', to: '/posts?status=open' },
            { type: 'link', label: 'menu.tickets.my', to: '/posts?responsability=operator' },
            { type: 'link', label: 'menu.tickets.create', to: '/post/create/forms', permission: 'posts.create' }
        ]
    },
    {
        type: 'group',
        label: 'menu.forms.form',
        icon: 'fal fa-file-alt',
        permission: 'forms.view',
        key: 'forms',
        children: [
            { type: 'link', label: 'menu.forms.all_forms', to: '/forms', exact: true },
            { type: 'link', label: 'menu.forms.create', to: '/forms/builder', permission: 'forms.create' },
            {
                type: 'link',
                label: 'menu.forms.form_types',
                to: '/form-types',
                // `form-types.view` n'autorise que la lecture de la taxonomie,
                // dont le menu et l'éditeur de formulaire ont besoin. L'écran
                // d'administration demande un droit de gestion.
                anyPermission: ['form-types.create', 'form-types.update', 'form-types.delete']
            }
        ]
    },
    {
        type: 'group',
        label: 'menu.statistics.statistic',
        icon: 'fal fa-chart-line',
        permission: 'company.statistics',
        key: 'stats',
        children: [
            { type: 'link', label: 'menu.statistics.dashboard', to: '/statistiques' },
            { type: 'link', label: 'menu.statistics.performance', to: '/performances' }
        ]
    },
    { type: 'link', label: 'menu.map', icon: 'fal fa-map-marker-alt', to: '/map', permission: 'posts.view' },

    {
        type: 'title',
        label: 'menu.parameters.name',
        anyPermission: ['users.view', 'company.settings', 'forms.view', 'roles.manage']
    },
    {
        type: 'group',
        label: 'menu.companies.company',
        icon: 'fal fa-building',
        anyPermission: ['users.view', 'company.settings', 'roles.manage'],
        key: 'company',
        children: [
            { type: 'link', label: 'menu.companies.back_office', to: '/company/users', permission: 'users.view' },
            { type: 'link', label: 'menu.companies.citizens', to: '/company/citizens', permission: 'users.view' },
            { type: 'link', label: 'menu.companies.settings', to: '/company/settings', permission: 'company.settings' },
            { type: 'link', label: 'menu.companies.webhooks', to: '/company/webhooks', permission: 'company.settings' },
            { type: 'link', label: 'menu.companies.roles', to: '/company/roles', permission: 'roles.manage' },
            { type: 'link', label: 'menu.companies.backoffice_infos', to: '/company/backoffice-infos', permission: 'company.settings' }
        ]
    },
    { type: 'link', label: 'menu.templates', icon: 'fal fa-envelope', to: '/admin/templates', permission: 'response-templates.view' },
    {
        type: 'group',
        label: 'menu.news.news',
        icon: 'fal fa-newspaper',
        anyPermission: ['news.view', 'news-categories.view'],
        key: 'news',
        children: [
            { type: 'link', label: 'menu.news.list', to: '/news', permission: 'news.view' },
            { type: 'link', label: 'menu.news.categories', to: '/news/categories', permission: 'news-categories.view' }
        ]
    },

    {
        type: 'group',
        label: 'menu.events.events',
        icon: 'fal fa-calendar-star',
        anyPermission: ['events.view'],
        key: 'events',
        children: [
            { type: 'link', label: 'menu.events.list', to: '/events', permission: 'events.view' },
            { type: 'link', label: 'menu.events.create', to: '/events/new', permission: 'events.create' }
        ]
    },

    { type: 'title', label: 'menu.superadmin.title', superAdminOnly: true },
    { type: 'link', label: 'menu.superadmin.companies', icon: 'fal fa-building', to: '/admin/companies', exact: false, superAdminOnly: true },
    { type: 'link', label: 'menu.superadmin.news', icon: 'fal fa-bullhorn', to: '/admin/news', exact: false, superAdminOnly: true }
]

/** Un élément de navigation est-il ouvert à cet utilisateur ? */
export function isNavItemAllowed(item, context = {}) {
    const permissions = context.permissions || []
    if (item.superAdminOnly && !context.isSuperAdmin) return false
    if (item.requiresContributionForm && !context.hasContributionForm) return false
    if (item.permission && !permissions.includes(item.permission)) return false
    if (item.anyPermission && !item.anyPermission.some((p) => permissions.includes(p))) return false
    return true
}

/**
 * Une entrée par section accessible, pour proposer des raccourcis : un groupe
 * pointe vers son premier enfant autorisé et emprunte son icône, les enfants
 * n'en ayant pas. Les titres de section et les destinations exclues sont
 * écartés.
 */
export function allowedNavSections(context = {}, options = {}) {
    const exclude = options.exclude || []
    return NAV.filter((item) => item.type !== 'title')
        .filter((item) => isNavItemAllowed(item, context))
        .map((item) => {
            if (item.type === 'link') {
                return { label: item.label, icon: item.icon, to: item.to }
            }
            const child = (item.children || []).find((candidate) =>
                isNavItemAllowed(candidate, context)
            )
            return child ? { label: item.label, icon: item.icon, to: child.to } : null
        })
        .filter((entry) => entry && entry.to && !exclude.includes(entry.to))
}
